import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { CSVLink } from 'react-csv';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './ClinicAiInsights.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Logo from '../../assets/loyaltap_logo.png';

const COLORS = ['#4CAF50', '#FFC107'];

const ClinicAiInsights = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');

  useEffect(() => {
    const fetchClinics = async () => {
      const snap = await getDocs(query(collection(db, 'clinics')));
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClinics(list);
      if (list.length > 0) setSelectedClinicId(list[0].id);
    };
    fetchClinics();
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!selectedClinicId) return;
      const q = query(
        collection(db, 'ai_suggestions'),
        where('clinic_id', '==', selectedClinicId),
        where('type', '==', 'clinic')
      );
      const snap = await getDocs(q);
      setSuggestions(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchSuggestions();
  }, [selectedClinicId]);

  const sentimentData = [
    { name: 'General', value: suggestions.length },
  ];

  const handlePdfExport = () => {
    const clinic = clinics.find(c => c.id === selectedClinicId);
    const clinicName = clinic ? clinic.name : selectedClinicId;

    const doc = new jsPDF();
    doc.text(`${t('clinicAiInsights.title')} - ${clinicName}`, 10, 10);
    const tableData = suggestions.map((s, i) => [
      i + 1,
      s.title,
      s.suggestion,
      new Date(s.createdAt.seconds * 1000).toLocaleString(),
    ]);
    doc.autoTable({
      head: [['#', t('clinicAiInsights.table_title'), t('clinicAiInsights.table_suggestion'), t('clinicAiInsights.table_date')]],
      body: tableData,
      startY: 20,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [76, 175, 80] }
    });
    doc.save(`ai_suggestions_${clinicName}.pdf`);
  };

  return (
    <div className="suggestions-container">
      <div className="logo">
        <img src={Logo} alt="LoyalTap Logo" className="logo-image" />
      </div>

      <h2>🤖 {t('clinicAiInsights.title')}</h2>
      <p style={{ color: 'gray', fontStyle: 'italic', marginBottom: '1rem' }}>
        {t('clinicAiInsights.note')}
      </p>

      <select
        value={selectedClinicId}
        onChange={(e) => setSelectedClinicId(e.target.value)}
        className="dropdown-clinic"
      >
        {clinics.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>

      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie data={sentimentData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
            {sentimentData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>

      <CSVLink
        data={suggestions}
        filename={`ai_suggestions_${selectedClinicId}.csv`}
        className="export-btn"
      >
        {t('clinicAiInsights.export_csv')}
      </CSVLink>

      <button className="export-btn2" onClick={handlePdfExport}>
        {t('clinicAiInsights.export_pdf')}
      </button>

      <button className="export-btn1" onClick={() => navigate('/admin/clinic-dashboard')}>
        {t('clinicAiInsights.back_to_dashboard')}
      </button>

      {suggestions.map((s) => (
        <div key={s.id} className="suggestion-card">
          <h3>{s.title}</h3>
          <div className="suggestion-content">
            {s.suggestion.split(/\d+\.\s/).filter(Boolean).map((text, idx) => (
              <p key={idx}><strong>{idx + 1}.</strong> {text.trim()}</p>
            ))}
          </div>
          {s.createdAt && (
            <small>{new Date(s.createdAt.seconds * 1000).toLocaleString()}</small>
          )}
        </div>
      ))}
    </div>
  );
};

export default ClinicAiInsights;